import { useEffect, useState } from "react";
import { Briefcase, MapPin, DollarSign, Lock, CheckCircle2, Upload, Building2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import TopNav from "@/components/TopNav";

export default function Jobs() {
  const [data, setData] = useState(null);
  const [applyJob, setApplyJob] = useState(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => { try { const { data } = await api.get("/jobs"); setData(data); } catch (e) { toast.error(apiErr(e)); } };
  useEffect(() => { load(); }, []);

  const apply = async () => {
    if (!file) return toast.error("Please upload your resume.");
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("resume", file);
      const { data } = await api.post(`/jobs/${applyJob.id}/apply`, fd);
      toast.success(data.message); setApplyJob(null); setFile(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!data) return <div className="min-h-screen"><TopNav /><div className="text-center mt-32 text-teal-accent">Loading…</div></div>;

  return (
    <div className="min-h-screen">
      <TopNav />
      <main className="max-w-6xl mx-auto px-4 sm:px-8 pt-8 pb-24">
        <span className="pill text-[11px] tracking-[0.2em] px-4 py-1.5 glass text-teal-accent inline-block">CAREER BOARD</span>
        <h1 className="serif text-4xl sm:text-5xl font-extrabold mt-5">Job vacancies</h1>

        {data.locked ? (
          <div className="glass-strong rounded-3xl p-12 mt-10 text-center">
            <div className="w-16 h-16 rounded-full bg-[#00D4D4]/10 flex items-center justify-center mx-auto mb-5"><Lock size={28} className="text-teal-accent" /></div>
            <h2 className="serif text-2xl font-bold">Career board locked</h2>
            <p className="text-muted-foreground mt-2">{data.message}</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">
            {data.jobs.map((j) => (
              <div key={j.id} data-testid={`job-${j.id}`} className="glass glass-hover rounded-3xl p-6 flex flex-col">
                <div className="flex items-start justify-between mb-4">
                  <Building2 size={26} className="text-teal-accent/70" />
                  <span className={`pill text-[10px] px-3 py-1 ${j.level === "SENIOR" ? "bg-yellow-500/15 text-yellow-300" : j.level === "MID" ? "bg-blue-500/15 text-blue-300" : "bg-[#00D4D4]/15 text-teal-accent"}`}>{j.level}</span>
                </div>
                <h3 className="serif text-2xl font-bold leading-tight">{j.title}</h3>
                <div className="text-sm text-muted-foreground">{j.company}</div>
                <div className="text-xs text-teal-accent mt-1">{j.category}</div>
                <div className="mt-4 space-y-1.5 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2"><MapPin size={14} /> {j.location} · {j.employment_type}</div>
                  {j.salary_range && <div className="flex items-center gap-2"><DollarSign size={14} /> {j.salary_range}</div>}
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  {(j.tags || []).map((t) => <span key={t} className="text-[11px] pill px-2.5 py-1 glass text-muted-foreground">{t}</span>)}
                </div>
                <button data-testid={`apply-${j.id}`} disabled={j.applied} onClick={() => setApplyJob(j)}
                        className="kiq-btn w-full py-3 mt-5 text-sm flex items-center justify-center gap-2">
                  {j.applied ? <><CheckCircle2 size={16} /> Applied</> : <>Apply now</>}
                </button>
              </div>
            ))}
          </div>
        )}
      </main>

      {applyJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" onClick={() => setApplyJob(null)}>
          <div className="glass-strong rounded-3xl p-7 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="apply-modal">
            <h3 className="serif text-2xl font-bold">{applyJob.title}</h3>
            <p className="text-sm text-muted-foreground">{applyJob.company} · {applyJob.location}</p>
            <label className="block mt-6 glass rounded-2xl border-dashed border-[#00D4D4]/30 p-6 text-center cursor-pointer hover:border-[#00D4D4]/60">
              <Upload size={24} className="text-teal-accent mx-auto mb-2" />
              <span className="text-sm">{file ? file.name : "Upload your resume (PDF/DOC)"}</span>
              <input data-testid="resume-input" type="file" className="hidden" accept=".pdf,.doc,.docx" onChange={(e) => setFile(e.target.files[0])} />
            </label>
            <button data-testid="submit-application" disabled={busy} onClick={apply} className="kiq-btn w-full py-3 mt-5 text-sm">{busy ? "Submitting…" : "Submit application"}</button>
          </div>
        </div>
      )}
    </div>
  );
}
